import React from "react";
import {
  makeStyles,
  createStyles,
  Theme,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Container,
  FormControlLabel,
  Button,
  Link,
  Tooltip,
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { Pagination } from "@material-ui/lab";
import {
  PageDisplay,
  ColumnBaseHeaderStyle,
  ColumnBaseCellStyle,
} from "../../../components";
import { WebCrawlingErrorPaneData, WebCrawlingErrorRow } from "../../../types/notification";
import { WebCrawlingAddExcludedUrlDialogState, WebCrawlingAddExcludedUrlDialog } from "./WebCrawlingAddExcludedUrlDialog";
import { ElectronAccessor } from "../../../helpers";

/** スタイル定義 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      minWidth: 650,
    },
    th: {
      ...ColumnBaseHeaderStyle,
      backgroundColor: theme.palette.background.paper,
    },
    td: ColumnBaseCellStyle,
    td_info: {
      ...ColumnBaseCellStyle,
      color: theme.palette.info.dark,
    },
    td_error: {
      ...ColumnBaseCellStyle,
      color: theme.palette.error.dark,
    },
    tdUrl: {
      ...ColumnBaseCellStyle,
      width: "auto",
      maxWidth: "0px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    tableContainer: {
      marginTop: theme.spacing(1),
    },
    pagination: {
      marginTop: theme.spacing(1),
    },
  })
);

/** Webクロール通知エラーペイン プロップ */
interface WebCrawlingErrorPaneProps {
  onOpenChange: (event: any) => void;
  errorData: WebCrawlingErrorPaneData;
  onCurrentPageChange: (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => void;
  addExcludedUrlDialogState: WebCrawlingAddExcludedUrlDialogState;
  onOpenAddExcludedUrlDialogChange: (
    webClawlingErrorId?: number,
    url?: string
  ) => void;
  onExcludedUrlChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAddExcludedUrlClick:() => void;
}

/** Webクロール通知エラーペイン */
export default function WebCrawlingErrorPane(props: WebCrawlingErrorPaneProps) {
  const classes = useStyles();

  //------------------------------
  // 表示切替関連
  const { onOpenChange } = props;

  //------------------------------
  // エラー画面関連
  const { errorData } = props;
  const { onCurrentPageChange } = props;

  //------------------------------
  // 除外URL登録ダイアログ関連
  const { addExcludedUrlDialogState } = props;
  const { onOpenAddExcludedUrlDialogChange } = props;
  const { onExcludedUrlChange } = props;
  const { onAddExcludedUrlClick } = props;

  /**
   * URL欄に表示するボタン取得
   * URLが除外URLに登録済みの場合はボタンはなし
   */
  const getUrlBotton = (row: WebCrawlingErrorRow) => {
    let component = <></>;
    if (row.error_url && row.category.indexOf("削除") === -1) {
      component = row.is_excluded ? (
        <div>除外URLに追加済み</div>
      ) : (
        <div>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => {
              onOpenAddExcludedUrlDialogChange(row.id, row.error_url);
            }}
          >
            除外URLに追加
          </Button>
        </div>
      );
    }
    return component;
  };

  //--------------------
  // コンポーネント
  return (
    <div>
      <Container maxWidth="lg">
        <FormControlLabel
          control={(
            <IconButton style={{ fontSize: "1rem" }} onClick={onOpenChange}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </IconButton>
          )}
          label={<span style={{ fontSize: "0.9rem" }}>戻る</span>}
        />
        <PageDisplay
          fromNumber={(errorData.currentPage - 1) * errorData.displayCount + 1}
          toNumber={
            errorData.currentPage * errorData.displayCount <
            errorData.totalCount
              ? errorData.currentPage * errorData.displayCount
              : errorData.totalCount
          }
          totalNumber={errorData.totalCount}
        />
        <TableContainer component={Paper} style={{ maxHeight: "80vh" }}>
          <Table
            className={classes.table}
            size="small"
            stickyHeader
            aria-label="a dense table"
          >
            <TableHead>
              <TableRow>
                <TableCell className={classes.th}>No</TableCell>
                <TableCell className={classes.th}>発生日時 </TableCell>
                <TableCell className={classes.th} style={{ minWidth: "200px" }}>URL</TableCell>
                <TableCell className={classes.th} style={{ minWidth: "100px" }}>区分</TableCell>
                <TableCell className={classes.th} style={{ minWidth: "60px" }}>レベル</TableCell>
                <TableCell className={classes.th}>メッセージ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errorData.rows.map((row, i) => (
                <TableRow key={row.id}>
                  <TableCell className={classes.td} component="th" scope="row">
                    {(errorData.currentPage - 1) * errorData.displayCount +
                      i +
                      1}
                  </TableCell>
                  <TableCell className={classes.td}>{row.created_at}</TableCell>
                  <TableCell className={classes.tdUrl}>
                    {row.error_url?(
                      <Tooltip title={row.error_url}>
                        <Link
                          href={row.error_url}
                          // [HP稼働]: electronからの修正
                          // onClick={(e: React.MouseEvent<HTMLElement>) => {
                          // e.preventDefault();
                          // ElectronAccessor.openExternal(
                          //   row.error_url
                          // );
                          // }}
                        >
                          {row.error_url}
                        </Link>
                      </Tooltip>
                    ):(<></>)}
                    {getUrlBotton(row)}
                  </TableCell>
                  <TableCell className={classes.td}>{row.category}</TableCell>
                  <TableCell className={row.level === "エラー" ? classes.td_error : classes.td_info}>{row.level}</TableCell>
                  <TableCell className={classes.td}>{row.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          className={classes.pagination}
          count={errorData.totalPage}
          page={errorData.currentPage}
          onChange={onCurrentPageChange}
        />
      </Container>
      <WebCrawlingAddExcludedUrlDialog
        state={addExcludedUrlDialogState}
        onOpenChange={onOpenAddExcludedUrlDialogChange}
        onUrlChange={onExcludedUrlChange}
        onAddExcludedUrlClick={onAddExcludedUrlClick}
      />
    </div>
  );
}
